import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import partial from 'lodash-es/partial';
import {
  Button,
  Text,
  VStack,
  Flex,
  Box,
  useToast,
  HStack,
  useDisclosure,
  Menu,
  MenuItemProps,
} from '@televet/kibble-ui';
import * as Sentry from '@sentry/react';
import Tooltip from 'shared/components/Tooltip';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ClinicSettingsTeamUserFragment,
  PermissionType,
  useGetClinicSettingsTeamUsersQuery,
  useResendUserInvitationMutation,
} from 'shared/types/graphql';
import { getUserFullName } from 'shared/utils';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ClinicAvatar } from 'shared/components/Avatars';
import EditUserModal from './EditUserModal';
import DeleteUserModal from './DeleteUserModal';
import DevModeDisplayJSON from '../../components/DevModeDisplayJSON';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsTeamEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';

enum UserMenuOptions {
  Edit = 'edit',
  Delete = 'delete',
}

const TeamSettings = (): ReactElement => {
  const { clinicUser, currentClinicId, userHasPermission, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { isOpen: isEditUserModalOpen, onOpen: onEditUserModalOpen, onClose: onEditUserModalClose } = useDisclosure();
  const {
    isOpen: isDeleteUserModalOpen,
    onOpen: onDeleteUserModalOpen,
    onClose: onDeleteUserModalClose,
  } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<ClinicSettingsTeamUserFragment>();
  const { trackEvent } = useTracking();
  const { isMobile } = useResolutionProvider();

  const hasWriteUserPermission = userHasPermission(PermissionType.WriteUser);
  const { data: teamUsersData, refetch: refetchTeamUsersData } = useGetClinicSettingsTeamUsersQuery({
    variables: {
      where: {
        vetInfo: {
          clinics: {
            some: {
              id: {
                equals: currentClinicId,
              },
            },
          },
        },
        isActive: {
          equals: true,
        },
      },
      clinicId: currentClinicId || '',
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const users = teamUsersData?.findManyUser || [];

  const toast = useToast();

  const [resendUserInvitation, { loading: isResendingUserInvitation }] = useResendUserInvitationMutation({
    refetchQueries: ['getClinicSettingsTeamUsers'],
    onCompleted: (data) => {
      toast({
        position: 'top',
        status: 'info',
        duration: 3000,
        description: `Invitation sent to ${data.resendUserInvitation.email}`,
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast({
        position: 'top',
        status: 'error',
        description: `Whoops! There was a problem and we were unable to resend the invitation. Please try again.`,
      });
    },
  });

  const handleResendInvitationClick = useCallback(
    (email: string) => {
      if (!currentClinicId) {
        return;
      }
      resendUserInvitation({ variables: { email, clinicId: currentClinicId } });
    },
    [currentClinicId, resendUserInvitation],
  );

  const editUser = useCallback((): void => {
    onEditUserModalOpen();
    trackEvent(SettingsTeamEvents.ViewEditUserModal);
  }, [onEditUserModalOpen, trackEvent]);

  const deleteUser = useCallback((): void => {
    onDeleteUserModalOpen();
    trackEvent(SettingsTeamEvents.ViewDeleteUserModal);
  }, [onDeleteUserModalOpen, trackEvent]);

  const onAddUserSuccess = (): void => {
    refetchTeamUsersData();
  };

  const userMenuDropdownOptions: MenuItemProps[] = useMemo(() => {
    return [
      {
        value: UserMenuOptions.Edit,
        label: 'Edit user',
        onSelect: editUser,
      },
      {
        value: UserMenuOptions.Delete,
        label: 'Delete user',
        onSelect: deleteUser,
      },
    ];
  }, [deleteUser, editUser]);

  return (
    <Box>
      <EditUserModal
        clinicId={currentClinicId}
        onAddUserSuccess={onAddUserSuccess}
        onEditUserSuccess={refetchTeamUsersData}
        isOpen={isEditUserModalOpen}
        onClose={(): void => {
          setSelectedUser(undefined);
          onEditUserModalClose();
        }}
        user={selectedUser}
      />
      <DeleteUserModal
        user={selectedUser}
        onConfirmDeleteUser={refetchTeamUsersData}
        isOpen={isDeleteUserModalOpen}
        onClose={(): void => {
          setSelectedUser(undefined);
          onDeleteUserModalClose();
        }}
      />
      <Box m="15px 0px 60px">
        <HStack justifyContent="space-between" p="20px 0px 30px">
          <Text size="md" fontWeight="semibold">
            Manage Team Members
          </Text>
          {hasWriteUserPermission && (
            <Button iconName="plus" variant="secondary" size="sm" onClick={(): void => editUser()}>
              Add User
            </Button>
          )}
        </HStack>
        <UserTable isMobile={isMobile}>
          <thead>
            <tr>
              <th>{isMobile ? 'Name/Email' : 'Name'}</th>
              {!isMobile && <th>Email</th>}
              <th>Role</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const { id, email, vetInfo, invitedAt, invitationCode } = user;
              const showResendInvitation = !!invitationCode && !!invitedAt;
              const role = vetInfo?.roles?.length ? vetInfo.roles[0].role.split('_').join(' ') : '';
              return (
                <React.Fragment key={id}>
                  <tr>
                    <td>
                      <Flex alignItems="center" justifyContent="normal">
                        <Box mr="15px">
                          <ClinicAvatar clinicMember={user} size="lg" />
                        </Box>
                        <Flex direction="column" align="flex-start">
                          <Box fontWeight="bold">
                            {getUserFullName(user)}
                            {user.id === clinicUser?.id && ' (you)'}
                          </Box>
                          {isMobile && <Box>{email}</Box>}
                        </Flex>
                      </Flex>
                    </td>
                    {!isMobile && (
                      <td>
                        <VStack direction="column" align="flex-start" spacing="1">
                          <Box>{email}</Box>
                          {showResendInvitation ? (
                            <Flex>
                              <Tooltip content={format(new Date(invitedAt), "cccc',' PP 'at' p")}>
                                <Box color="text.subtle">
                                  Invitation sent{' '}
                                  {formatDistanceToNow(new Date(invitedAt), {
                                    addSuffix: true,
                                  })}
                                </Box>
                              </Tooltip>
                              {hasAdminRoleAtCurrentClinic && (
                                <Button
                                  ml="2"
                                  variant="ghost"
                                  size="xs"
                                  disabled={isResendingUserInvitation}
                                  onClick={partial(handleResendInvitationClick, email)}
                                >
                                  Resend
                                </Button>
                              )}
                            </Flex>
                          ) : null}
                        </VStack>
                      </td>
                    )}
                    <td>
                      <Box>{role}</Box>
                    </td>
                    {hasWriteUserPermission && (
                      <td>
                        <Menu
                          listProps={{ menuItems: userMenuDropdownOptions }}
                          buttonProps={{
                            variant: 'ghostNeutral',
                            showRightIcon: false,
                            leftIconName: 'dotsVertical',
                            onClick: (): void => {
                              setSelectedUser(user);
                            },
                          }}
                        />
                      </td>
                    )}
                  </tr>
                  <DevModeDisplayJSON src={user} />
                </React.Fragment>
              );
            })}
          </tbody>
        </UserTable>
      </Box>
    </Box>
  );
};

export default TeamSettings;

const UserTable = styled.table<{ isMobile: boolean }>`
  width: 100%;
  text-align: left;
  vertical-align: middle;
  border-collapse: collapse;
  th {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: normal;
    padding: 5px 10px;
  }
  td {
    padding: ${({ isMobile }): string => (isMobile ? '8px 4px' : '15px 10px')};
    font-size: 14px;
  }
  thead tr {
    border-bottom: 1px solid #e8e9e9;
  }
  tbody tr:nth-of-type(2n) {
    background: var(--chakra-colors-background-default);
  }
`;
